/**
 * This strip displays `AgendaDia` from `agendaDePrevisao` (the one forecast read).
 * It does not count.
 */
import type { AgendaDia } from "./home/revisaoHome";
import { REVISAO_FILTER_FOCUS_RING, revisaoFilterCx } from "./filtros/tokens";

export interface AgendaFitaProps {
  /** Days from `agendaDePrevisao`. Render every item, including total 0. Do not drop, pad, or recount. */
  dias: readonly AgendaDia[];
  /** ISO of the pinned day, or null. */
  diaFixado?: string | null;
  /** Toggle pin. Called with the day's iso, or null to unpin. Omit in stories. */
  onFixarDia?: (iso: string | null) => void;
  /** Practice that day. Omit in stories. */
  onPraticarDia?: (iso: string) => void;
}

export default function AgendaFita({
  dias,
  diaFixado = null,
  onFixarDia,
  onPraticarDia,
}: AgendaFitaProps) {
  return (
    <div role="list" aria-label="Próximos sete dias" className="min-w-0 overflow-x-auto">
      <div role="presentation" className="flex w-max gap-2 xl:w-full">
        {dias.map((dia) => {
          const fixado = dia.iso === diaFixado;
          const destacado = dia.hoje || fixado;
          return (
            <div
              key={dia.iso}
              role="listitem"
              aria-current={dia.hoje ? "date" : undefined}
              className={revisaoFilterCx(
                "flex w-[6.75rem] shrink-0 flex-col gap-1 rounded-card border px-2 py-3",
                "xl:w-auto xl:min-w-0 xl:flex-1",
                destacado
                  ? "border-select/[0.42] bg-select/[0.14] text-select-ink"
                  : "border-edge bg-canvas text-ink",
              )}
            >
              <span className="whitespace-nowrap text-sm font-semibold">{dia.label}</span>
              {fixado ? (
                <span className="self-start rounded-full border border-select/[0.42] px-2 py-0.5 text-[11px] font-semibold">
                  Fixado
                </span>
              ) : null}
              <time
                dateTime={dia.iso}
                className={revisaoFilterCx("text-xs", !destacado && "text-ink-secondary")}
              >
                {dia.date}
              </time>
              <span className="text-lg font-semibold tabular-nums leading-none">
                {dia.total}
                <span className="sr-only"> cartões</span>
              </span>
              {onFixarDia || onPraticarDia ? (
                <div className="mt-auto flex flex-col gap-1 pt-2">
                  {onFixarDia ? (
                    <button
                      type="button"
                      aria-pressed={fixado}
                      aria-label={`Fixar ${dia.label}`}
                      onClick={() => onFixarDia(fixado ? null : dia.iso)}
                      className={revisaoFilterCx(
                        "h-8 rounded-chip border px-2 text-[12px] font-medium",
                        REVISAO_FILTER_FOCUS_RING,
                        fixado
                          ? "border-select/[0.42] bg-select/[0.14] text-select-ink"
                          : "border-edge bg-canvas text-ink-secondary",
                      )}
                    >
                      Fixar
                    </button>
                  ) : null}
                  {onPraticarDia ? (
                    <button
                      type="button"
                      aria-label={`Praticar ${dia.label}`}
                      disabled={dia.decks.length === 0}
                      onClick={() => onPraticarDia(dia.iso)}
                      className={revisaoFilterCx(
                        "h-8 rounded-chip border border-edge bg-canvas px-2 text-[12px] font-medium text-ink",
                        "hover:border-edge-strong hover:bg-surface-hover",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "disabled:hover:border-edge disabled:hover:bg-canvas",
                        REVISAO_FILTER_FOCUS_RING,
                      )}
                    >
                      Praticar
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
